/**
 * parseHtmlChunks.js
 *
 * Parses an HTML string into an array of renderable chunks for virtualization.
 * Each chunk is a top-level section (article, div, etc.) from the document body.
 * Styles and head content are extracted separately for injection into the wrapper.
 */

/**
 * Parse HTML content into chunks suitable for virtualized rendering.
 * @param {string} htmlString - The full HTML document string
 * @returns {{ styles: string, chunks: Array<{ id: string, html: string, articleNumber: string|null, index: number }> }}
 */
export function parseHtmlChunks(htmlString) {
  if (!htmlString) return { styles: '', dir: '', chunks: [] }

  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')

  // Extract document direction from <html dir="..."> attribute
  const htmlDir = doc.documentElement?.getAttribute('dir') || ''

  // Extract all styles (inline <style> tags + <link rel="stylesheet">)
  const styleTags = doc.querySelectorAll('style')
  const linkTags = doc.querySelectorAll('link[rel="stylesheet"]')
  let styles = ''
  styleTags.forEach(tag => {
    styles += tag.outerHTML + '\n'
  })
  linkTags.forEach(tag => {
    styles += tag.outerHTML + '\n'
  })

  // Get body children as chunks
  const body = doc.body
  if (!body) return { styles, dir: htmlDir, chunks: [] }

  const children = Array.from(body.children)

  // If document has .article elements, use those as chunk boundaries
  const articles = body.querySelectorAll('.article, [data-article]')

  if (articles.length > 5) {
    // Article-based chunking: group consecutive non-article elements as preamble/interstitial chunks
    return { styles, dir: htmlDir, chunks: chunkByArticles(body) }
  }

  // Fallback: use top-level body children as chunks
  // Group small consecutive elements together to avoid too many tiny chunks
  return { styles, dir: htmlDir, chunks: chunkByTopLevel(children) }
}

/**
 * Chunk by .article / [data-article] elements.
 * Non-article content between articles gets grouped into interstitial chunks.
 */
function chunkByArticles(body) {
  const chunks = []
  let currentNonArticle = []
  let chunkIndex = 0

  const flushNonArticle = () => {
    if (currentNonArticle.length === 0) return
    const html = currentNonArticle.map(el => el.outerHTML).join('\n')
    chunks.push({
      id: `chunk-${chunkIndex}`,
      html,
      articleNumber: null,
      index: chunkIndex
    })
    chunkIndex++
    currentNonArticle = []
  }

  const walkChildren = (parent) => {
    Array.from(parent.children).forEach(child => {
      const isArticle = child.classList.contains('article') || child.hasAttribute('data-article')

      if (isArticle) {
        flushNonArticle()
        const articleNum = child.getAttribute('data-article') || null
        chunks.push({
          id: articleNum ? `article-${articleNum}` : `chunk-${chunkIndex}`,
          html: child.outerHTML,
          articleNumber: articleNum,
          index: chunkIndex
        })
        chunkIndex++
      } else {
        // Check if this element contains articles (e.g., a wrapper div)
        const nestedArticles = child.querySelectorAll('.article, [data-article]')
        if (nestedArticles.length > 0) {
          // Recurse into this container
          flushNonArticle()
          walkChildren(child)
        } else {
          currentNonArticle.push(child)
        }
      }
    })
  }

  walkChildren(body)
  flushNonArticle()

  return chunks
}

/**
 * Fallback chunking: group top-level children.
 * Merges small consecutive elements to keep chunk count reasonable.
 */
function chunkByTopLevel(children) {
  const MIN_CHUNK_TEXT_LENGTH = 200
  const chunks = []
  let currentGroup = []
  let currentTextLength = 0
  let chunkIndex = 0

  const flush = () => {
    if (currentGroup.length === 0) return
    const html = currentGroup.map(el => el.outerHTML).join('\n')
    chunks.push({
      id: `chunk-${chunkIndex}`,
      html,
      articleNumber: null,
      index: chunkIndex
    })
    chunkIndex++
    currentGroup = []
    currentTextLength = 0
  }

  children.forEach(child => {
    currentGroup.push(child)
    currentTextLength += (child.textContent || '').length

    // Flush if this element is substantial enough on its own
    if (currentTextLength >= MIN_CHUNK_TEXT_LENGTH) {
      flush()
    }
  })

  flush()
  return chunks
}
